
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Crown, RefreshCw, Stars, Moon, Luggage, MapPin, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getRewardfulReferral } from '@/utils/rewardful';
import { redirectToCheckout } from '@/lib/analytics';
import { useToast } from '@/hooks/use-toast';

interface SubscriptionFallbackProps {
  type: 'loading' | 'error' | 'not-subscribed' | 'canceled';
  onRetry?: () => void;
  onManageSubscription?: () => void;
}

export const SubscriptionFallback: React.FC<SubscriptionFallbackProps> = ({ type, onRetry, onManageSubscription }) => {
  const { toast } = useToast();

  const handleUpgrade = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { referralId: getRewardfulReferral() }
      });
      
      if (error) {
        console.error('Error creating checkout:', error);
        toast({
          title: 'Error',
          description: 'Failed to start upgrade process. Please try again.',
          variant: 'destructive',
        });
        return;
      }
      
      if (data?.url) {
        redirectToCheckout(data.url, 'Premium');
      } else {
        console.error('No checkout URL received');
        toast({
          title: 'Error',
          description: 'Failed to get checkout URL.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: 'Error',
        description: 'Failed to start upgrade process.',
        variant: 'destructive',
      });
    }
  };

  if (type === 'loading') {
    return (
      <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center text-amber-800">
            <RefreshCw className="w-6 h-6 mr-3 animate-spin" />
            Loading Subscription Information...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-amber-700">
            Please wait while we fetch your subscription details from Stripe.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (type === 'error') {
    return (
      <Card className="bg-gradient-to-r from-orange-50 to-red-50 border-orange-200">
        <CardHeader>
          <CardTitle className="flex items-center text-orange-800">
            <AlertCircle className="w-6 h-6 mr-3" />
            Subscription Information Unavailable
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge className="bg-orange-500 text-white">
              <Crown className="w-3 h-3 mr-1" />
              Premium Member
            </Badge>
            <span className="text-orange-700">
              You have an active subscription, but we couldn't load the details.
            </span>
          </div>
          <p className="text-orange-700">
            This might be a temporary connectivity issue with our payment provider. 
            Your subscription is still active and you have full access to all premium features.
          </p>
          <div className="flex gap-3">
            {onRetry && (
              <Button onClick={onRetry} className="bg-orange-600 hover:bg-orange-700 text-white">
                <Crown className="w-4 h-4 mr-2" />
                Manage Subscription
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => window.location.reload()}
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Page
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (type === 'canceled') {
    return (
      <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center text-red-800">
            <AlertCircle className="w-6 h-6 mr-3" />
            Subscription Canceled
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-red-300 text-red-700">
              Canceled
            </Badge>
            <span className="text-red-700">
              Your premium subscription has been canceled
            </span>
          </div>
          
          <p className="text-red-700">
            You previously had a premium subscription that has been canceled. 
            You can reactivate it or start a new subscription to regain access to premium features.
          </p>

          <div className="space-y-3">
            <h4 className="font-semibold text-red-800">What you're missing:</h4>
            <ul className="space-y-2 text-red-700">
              <li className="flex items-center gap-2">
                <Stars className="w-4 h-4 text-gold" />
                <strong>Milky Way Planner</strong> – Visibility ratings & best nights
              </li>
              <li className="flex items-center gap-2">
                <Moon className="w-4 h-4 text-amber-500" />
                <strong>Moon Planner</strong> – Supermoon & golden hour tracking
              </li>
              <li className="flex items-center gap-2">
                <Luggage className="w-4 h-4 text-teal-500" />
                <strong>Trip Planner</strong> – Multi-stop itineraries
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gold" />
                Unlimited saved locations
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            {onManageSubscription && (
              <Button 
                onClick={onManageSubscription}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                <Crown className="w-4 h-4 mr-2" />
                Manage Subscription
              </Button>
            )}
            <Button
              variant="premium"
              onClick={handleUpgrade}
              className="w-full"
            >
              <Crown className="w-4 h-4 mr-2" />
              Start New Subscription
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // type === 'not-subscribed'
  return (
    <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
      <CardHeader>
        <CardTitle className="flex items-center text-amber-800">
          <Crown className="w-6 h-6 mr-3" />
          Get Premium
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-amber-700">
          Unlock unlimited access to all three planners, saved locations, and advanced exports.
        </p>
        
        <div className="space-y-3">
          <h4 className="font-semibold text-amber-800">Premium Benefits:</h4>
          <ul className="space-y-2 text-amber-700">
            <li className="flex items-center gap-2">
              <Stars className="w-4 h-4 text-gold" />
              <strong>Milky Way Planner</strong> – Visibility ratings & best nights
            </li>
            <li className="flex items-center gap-2">
              <Moon className="w-4 h-4 text-amber-500" />
              <strong>Moon Planner</strong> – Supermoon & golden hour tracking
            </li>
            <li className="flex items-center gap-2">
              <Luggage className="w-4 h-4 text-teal-500" />
              <strong>Trip Planner</strong> – Multi-stop itineraries
            </li>
            <li className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gold" />
              Save unlimited favorite locations
            </li>
            <li className="flex items-center gap-2">
              <Download className="w-4 h-4 text-gold" />
              Export to Excel, CSV & Calendar formats
            </li>
          </ul>
        </div>

        <Button
          variant="premium"
          onClick={handleUpgrade}
          className="w-full"
        >
          <Crown className="w-4 h-4 mr-2" />
          Get Premium — $25/year
        </Button>
      </CardContent>
    </Card>
  );
};
